import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tags, Check, Receipt, CheckSquare } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { Switch } from '../../ui/switch';
import { TASK_TYPE_ICONS as taskTypeIcons } from '../../../lib/constants';

const TaskTypePicker = ({ value, taskTypes = [], needsInvoice, onChange, onNeedsInvoiceChange, children, disabled = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selectedType = taskTypes.find(x => x.id === value);
  const IconComponent = selectedType ? (taskTypeIcons[selectedType.icon] || CheckSquare) : Tags;

  const defaultTrigger = (
    <button disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent/60'}`}
      style={{ color: selectedType?.color || '#9CA3AF' }}
    >
      <IconComponent className="w-3.5 h-3.5" />
      <span>{selectedType?.name || t('tasks.type')}</span>
      {needsInvoice && <Receipt className="w-3 h-3 text-green-400" />}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 bg-accent border-border max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none" align="start" sideOffset={4}>
        <div className="space-y-0.5">
          {taskTypes.map(type => {
            const TypeIcon = taskTypeIcons[type.icon] || CheckSquare;
            return (
              <button
                key={type.id}
                onClick={() => {
                  onChange(type.id);
                  if (needsInvoice === null || needsInvoice === undefined) {
                    onNeedsInvoiceChange?.(type.needs_invoice_default);
                  }
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm ${value === type.id ? 'bg-accent/60' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: `${type.color}20` }}>
                    <TypeIcon className="w-3.5 h-3.5" style={{ color: type.color }} />
                  </div>
                  <div className="text-left">
                    <div className="text-foreground/80 text-xs">{type.name}</div>
                  </div>
                </div>
                {value === type.id && <Check className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" />}
              </button>
            );
          })}
        </div>
        <div className="border-t border-border mt-2 pt-2">
          <div className="flex items-center justify-between px-2.5 py-1">
            <div className="flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-foreground/80">{t('tasks.billable')}</span>
            </div>
            <Switch
              checked={!!needsInvoice}
              onCheckedChange={(v) => onNeedsInvoiceChange?.(v)}
              aria-label={t('tasks.billable')}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TaskTypePicker;
